import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum CheckoutOptimizationWidgetType {
  FULFILLMENT_CUTOFF = "FULFILLMENT_CUTOFF",
  THRESHOLD_UPSELL = "THRESHOLD_UPSELL",
  LOW_PROFILE_PRODUCT_UPSELL = "LOW_PROFILE_PRODUCT_UPSELL",
  CONTENT_BLOCK = "CONTENT_BLOCK",
  IMAGE_BLOCK = "IMAGE_BLOCK",
  SHIPPING_OPTION_CONTENT = "SHIPPING_OPTION_CONTENT",
  SHIPPING_OPTION_IMAGE = "SHIPPING_OPTION_IMAGE",
  SHIPPING_OPTION_PRODUCT_UPSELL = "SHIPPING_OPTION_PRODUCT_UPSELL",
  FAILED_DISCOUNT_RESCUE = "FAILED_DISCOUNT_RESCUE",
  PRODUCT_REVIEWS = "PRODUCT_REVIEWS",
}

export enum CheckoutReviewsProvider {
  JUDGE_ME = "JUDGE_ME",
  OKENDO = "OKENDO",
  YOTPO = "YOTPO",
}

export const checkoutReviewsProviderToLabel: Record<
  CheckoutReviewsProvider,
  string
> = {
  [CheckoutReviewsProvider.JUDGE_ME]: "Judge.me",
  [CheckoutReviewsProvider.OKENDO]: "Okendo",
  [CheckoutReviewsProvider.YOTPO]: "Yotpo",
};

export const checkoutOptimizationWidgetTypeToLabel: Record<
  CheckoutOptimizationWidgetType,
  string
> = {
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: "Fulfillment cutoff",
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: "Upsell",
  [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]:
    "Low-profile product upsell",
  [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: "Content block",
  [CheckoutOptimizationWidgetType.IMAGE_BLOCK]: "Image block",
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]:
    "Shipping option content",
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]:
    "Shipping option image",
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL]:
    "Shipping option upsell",
  [CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE]:
    "Failed discount rescue",
  [CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]: "Product reviews",
};

// targets for purchase.checkout.block.render (as of Mar. 2025)
export enum PurchaseCheckoutBlockRenderTarget {
  INFORMATION1 = "INFORMATION1",
  INFORMATION2 = "INFORMATION2",
  DELIVERY1 = "DELIVERY1",
  PAYMENT1 = "PAYMENT1",
  PAYMENT2 = "PAYMENT2",
  ORDER_SUMMARY1 = "ORDER_SUMMARY1",
  ORDER_SUMMARY2 = "ORDER_SUMMARY2",
  ORDER_SUMMARY3 = "ORDER_SUMMARY3",
  ORDER_SUMMARY4 = "ORDER_SUMMARY4",
  REDUCTIONS_BEFORE = "REDUCTIONS_BEFORE",
  REDUCTIONS_AFTER = "REDUCTIONS_AFTER",
}

export const PurchaseCheckoutBlockRenderTargetToDisplayText: Record<
  PurchaseCheckoutBlockRenderTarget,
  string
> = {
  [PurchaseCheckoutBlockRenderTarget.INFORMATION1]: "Information 1",
  [PurchaseCheckoutBlockRenderTarget.INFORMATION2]: "Information 2",
  [PurchaseCheckoutBlockRenderTarget.DELIVERY1]: "Delivery 1",
  [PurchaseCheckoutBlockRenderTarget.PAYMENT1]: "Payment 1",
  [PurchaseCheckoutBlockRenderTarget.PAYMENT2]: "Payment 2",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1]: "Order summary 1",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY2]: "Order summary 2",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY3]: "Order summary 3",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY4]: "Order summary 4",
  [PurchaseCheckoutBlockRenderTarget.REDUCTIONS_BEFORE]: "Reductions (above)",
  [PurchaseCheckoutBlockRenderTarget.REDUCTIONS_AFTER]: "Reductions (below)",
};

/**
 * Display text for a widget render target. Widgets can now be placed at
 * arbitrary string targets (CRO-1578), so this falls back to the raw target
 * string when it is not one of the known `PurchaseCheckoutBlockRenderTarget`
 * values.
 */
export function purchaseCheckoutBlockRenderTargetDisplayText(
  target: string,
): string {
  const knownDisplayText: Record<string, string> =
    PurchaseCheckoutBlockRenderTargetToDisplayText;
  return knownDisplayText[target] ?? target;
}

// targets for purchase.checkout.shipping-option-item.* (as of Dec. 2025)
export enum ShippingOptionRenderTarget {
  DETAILS_RENDER_AFTER = "DETAILS_RENDER_AFTER",
  DETAILS_RENDER_EXPANDED = "DETAILS_RENDER_EXPANDED",
}

export const ShippingOptionRenderTargetToDisplayText: Record<
  ShippingOptionRenderTarget,
  string
> = {
  [ShippingOptionRenderTarget.DETAILS_RENDER_AFTER]: "Inside the block",
  [ShippingOptionRenderTarget.DETAILS_RENDER_EXPANDED]: "After the block",
};

export const CHECKOUT_IMAGE_BLOCK_MIN_WIDTH_PX = 30;
export const CHECKOUT_IMAGE_BLOCK_MAX_WIDTH_PX = 360;
export const CHECKOUT_IMAGE_BLOCK_DEFAULT_WIDTH_PX = 180;
export const CHECKOUT_IMAGE_BLOCK_WIDTH_FULL = "FULL";

export const checkoutImageBlockWidthPxOptions = [
  30, 60, 90, 120, 180, 240, 300, 360,
] as const;

export const checkoutImageBlockWidthOptions = [
  "30",
  "60",
  "90",
  "120",
  "180",
  "240",
  "300",
  "360",
  CHECKOUT_IMAGE_BLOCK_WIDTH_FULL,
] as const;
export type CheckoutImageBlockWidth =
  (typeof checkoutImageBlockWidthOptions)[number];

export const checkoutImageBlockWidthToDisplayText: Record<
  CheckoutImageBlockWidth,
  string
> = {
  "30": "30px",
  "60": "60px",
  "90": "90px",
  "120": "120px",
  "180": "180px",
  "240": "240px",
  "300": "300px",
  "360": "360px",
  [CHECKOUT_IMAGE_BLOCK_WIDTH_FULL]: "Full width",
};

export enum CheckoutImageBlockJustification {
  LEFT = "LEFT",
  CENTER = "CENTER",
  RIGHT = "RIGHT",
}

export const CheckoutImageBlockJustificationToDisplayText: Record<
  CheckoutImageBlockJustification,
  string
> = {
  [CheckoutImageBlockJustification.LEFT]: "Left",
  [CheckoutImageBlockJustification.CENTER]: "Center",
  [CheckoutImageBlockJustification.RIGHT]: "Right",
};

export const FULFILLMENT_CUTOFF_WIDGET_ORDER_CUTOFF_TIME_PLACEHOLDER =
  "{{order_cutoff_time}}";
export const FULFILLMENT_CUTOFF_WIDGET_DAY_OF_ORDER_CUTOFF_TIME_PLACEHOLDER =
  "{{day_of_order_cutoff_time}}";
export const FULFILLMENT_CUTOFF_WIDGET_FASTEST_DELIVERY_DATE_PLACEHOLDER =
  "{{fastest_delivery_date}}";

const CheckoutOptimizationFulfillmentCutoffWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
  _id: zExt.objectId(),
  text: z.string(),
  backgroundColor: z.string(),
  iconColor: z.string(),
});
export const CheckoutOptimizationFulfillmentCutoffWidgetSchema: z.ZodType<CheckoutOptimizationFulfillmentCutoffWidget> =
  CheckoutOptimizationFulfillmentCutoffWidgetSchema_;
export interface CheckoutOptimizationFulfillmentCutoffWidget
  extends z.infer<typeof CheckoutOptimizationFulfillmentCutoffWidgetSchema_> {}

export const THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER =
  "{{amount_left}}";

export const THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT = "Add";
export const LOW_PROFILE_PRODUCT_UPSELL_MAX_ITEMS = 3;

export enum ManualProductSourceType {
  REDO_AI = "REDO_AI",
  COLLECTION = "COLLECTION",
  PRODUCT = "PRODUCT",
  TAGS = "TAGS",
}

const thresholdManualProductSource: ManualProductSourceType[] = [
  ManualProductSourceType.COLLECTION,
  ManualProductSourceType.PRODUCT,
  ManualProductSourceType.TAGS,
] as const;
export type ThresholdManualProductSourceType =
  (typeof thresholdManualProductSource)[number];

const lowProfileManualProductSource: ManualProductSourceType[] = [
  ManualProductSourceType.REDO_AI,
  ManualProductSourceType.COLLECTION,
  ManualProductSourceType.PRODUCT,
  ManualProductSourceType.TAGS,
] as const;
export type LowProfileManualProductSourceType =
  (typeof lowProfileManualProductSource)[number];

/**
 * Configuration for a product-level discount applied at runtime when a shopper
 * clicks Add on an upsell widget slot. The resulting discount is scoped to
 * whichever product variant was just added to cart, regardless of how the slot
 * resolved the product (manual PRODUCT, AI, collection, or tag pool).
 */
export enum UpsellProductDiscountValueType {
  PERCENTAGE = "PERCENTAGE",
  AMOUNT = "AMOUNT",
}

const UpsellProductDiscountPercentageSchema_ = z.object({
  valueType: z.literal(UpsellProductDiscountValueType.PERCENTAGE),
  value: z.number(),
});
const UpsellProductDiscountAmountSchema_ = z.object({
  valueType: z.literal(UpsellProductDiscountValueType.AMOUNT),
  value: z.number(),
  currency: z.string(),
});
const UpsellProductDiscountSchema_ = z.discriminatedUnion("valueType", [
  UpsellProductDiscountPercentageSchema_,
  UpsellProductDiscountAmountSchema_,
]);
export const UpsellProductDiscountSchema: z.ZodType<UpsellProductDiscount> =
  UpsellProductDiscountSchema_;
export type UpsellProductDiscount = z.infer<
  typeof UpsellProductDiscountSchema_
>;

/**
 * Returns the discount unchanged when it represents an actual reduction
 * (`value > 0`). Returns `undefined` for zero, negative, NaN, or missing
 * discounts so callers can persist "no discount" instead of a noop $0/0%
 * config. Persisting `value: 0` would cause the storefront to render the
 * discount UI (badge, strikethrough, "discounted price") with a no-op price,
 * which is what a merchant ends up with after clicking "Add discount" without
 * editing the value.
 */
export function normalizeUpsellProductDiscount(
  discount: UpsellProductDiscount | null | undefined,
): UpsellProductDiscount | undefined {
  if (!discount) {
    return undefined;
  }
  if (!Number.isFinite(discount.value) || discount.value <= 0) {
    return undefined;
  }
  return discount;
}

const ManualProductSourceRedoAiSchema_ = z.object({
  type: z.literal(ManualProductSourceType.REDO_AI),
  discount: UpsellProductDiscountSchema_.nullish(),
});
export const ManualProductSourceRedoAiSchema: z.ZodType<ManualProductSourceRedoAi> =
  ManualProductSourceRedoAiSchema_;
export interface ManualProductSourceRedoAi
  extends z.infer<typeof ManualProductSourceRedoAiSchema_> {}

const ManualProductSourceCollectionSchema_ = z.object({
  type: z.literal(ManualProductSourceType.COLLECTION),
  collectionId: z.string(),
  collectionTitle: z.string().optional(),
  fallbackIfNotAvailable: z.boolean().optional(),
  discount: UpsellProductDiscountSchema_.nullish(),
});
export const ManualProductSourceCollectionSchema: z.ZodType<ManualProductSourceCollection> =
  ManualProductSourceCollectionSchema_;
export interface ManualProductSourceCollection
  extends z.infer<typeof ManualProductSourceCollectionSchema_> {}

const ManualProductSourceProductSchema_ = z.object({
  type: z.literal(ManualProductSourceType.PRODUCT),
  productId: z.string(),
  productTitle: z.string().optional(),
  productImageUrl: z.string().optional(),
  fallbackIfNotAvailable: z.boolean().optional(),
  discount: UpsellProductDiscountSchema_.nullish(),
});
export const ManualProductSourceProductSchema: z.ZodType<ManualProductSourceProduct> =
  ManualProductSourceProductSchema_;
export interface ManualProductSourceProduct
  extends z.infer<typeof ManualProductSourceProductSchema_> {}

const ManualProductSourceTagsSchema_ = z.object({
  type: z.literal(ManualProductSourceType.TAGS),
  tags: z.array(z.string()),
  fallbackIfNotAvailable: z.boolean().optional(),
  discount: UpsellProductDiscountSchema_.nullish(),
});
export const ManualProductSourceTagsSchema: z.ZodType<ManualProductSourceTags> =
  ManualProductSourceTagsSchema_;
export interface ManualProductSourceTags
  extends z.infer<typeof ManualProductSourceTagsSchema_> {}

const manualProductSourceSchema = z.discriminatedUnion("type", [
  ManualProductSourceRedoAiSchema_,
  ManualProductSourceCollectionSchema_,
  ManualProductSourceProductSchema_,
  ManualProductSourceTagsSchema_,
]);
export type ManualProductSource = z.infer<typeof manualProductSourceSchema>;

export const ThresholdManualProductSourceSchema_ = z.discriminatedUnion(
  "type",
  [
    ManualProductSourceCollectionSchema_,
    ManualProductSourceProductSchema_,
    ManualProductSourceTagsSchema_,
  ],
);
export const ThresholdManualProductSourceSchema: z.ZodType<ThresholdManualProductSource> =
  ThresholdManualProductSourceSchema_;
export type ThresholdManualProductSource = z.infer<
  typeof ThresholdManualProductSourceSchema_
>;

export const lowProfileManualProductSourceSchema_ = z.discriminatedUnion(
  "type",
  [
    ManualProductSourceRedoAiSchema_,
    ManualProductSourceCollectionSchema_,
    ManualProductSourceProductSchema_,
    ManualProductSourceTagsSchema_,
  ],
);
export const lowProfileManualProductSourceSchema: z.ZodType<LowProfileManualProductSource> =
  lowProfileManualProductSourceSchema_;
export type LowProfileManualProductSource = z.infer<
  typeof lowProfileManualProductSourceSchema_
>;

const CheckoutOptimizationThresholdUpsellWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
  _id: zExt.objectId(),
  threshold: z.object({ amount: z.number(), currency: z.string() }),
  gteThresholdText: z.string(),
  ltThresholdText: z.string(),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  progressBarColor: z
    .object({ filled: z.string().optional(), unfilled: z.string().optional() })
    .optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z.array(ThresholdManualProductSourceSchema_).optional(),
  /**
   * Applied to every slot's resolved product at click time unless the slot has
   * its own `discount`. Useful when the widget runs without manual sources and
   * auto-fills from the tag pool — in that mode there are no slots to hang a
   * discount on. Only supported on THRESHOLD_UPSELL; LOW_PROFILE configures
   * discounts per-slot.
   */
  defaultProductDiscount: UpsellProductDiscountSchema_.optional(),
});
export const CheckoutOptimizationThresholdUpsellWidgetSchema: z.ZodType<CheckoutOptimizationThresholdUpsellWidget> =
  CheckoutOptimizationThresholdUpsellWidgetSchema_;
export interface CheckoutOptimizationThresholdUpsellWidget
  extends z.infer<typeof CheckoutOptimizationThresholdUpsellWidgetSchema_> {}

const CheckoutOptimizationLowProfileProductUpsellWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL),
  _id: zExt.objectId(),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z
    .array(lowProfileManualProductSourceSchema_)
    .optional(),
});
export const CheckoutOptimizationLowProfileProductUpsellWidgetSchema: z.ZodType<CheckoutOptimizationLowProfileProductUpsellWidget> =
  CheckoutOptimizationLowProfileProductUpsellWidgetSchema_;
export interface CheckoutOptimizationLowProfileProductUpsellWidget
  extends z.infer<
    typeof CheckoutOptimizationLowProfileProductUpsellWidgetSchema_
  > {}

const ContentBlockSectionSchema = z.object({
  _id: z.string(),
  title: z.string().nullish(),
  description: z.string().nullish(),
  imageUrl: z.string().nullish(),
});

const CheckoutOptimizationContentBlockWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
  _id: zExt.objectId(),
  sections: z.array(ContentBlockSectionSchema).max(5),
});
export const CheckoutOptimizationContentBlockWidgetSchema: z.ZodType<CheckoutOptimizationContentBlockWidget> =
  CheckoutOptimizationContentBlockWidgetSchema_;
export interface CheckoutOptimizationContentBlockWidget
  extends z.infer<typeof CheckoutOptimizationContentBlockWidgetSchema_> {}

const CheckoutOptimizationImageBlockWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.IMAGE_BLOCK),
  _id: zExt.objectId(),
  imageUrl: z.string().nullish(),
  imageWidthPx: z.coerce.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});
export const CheckoutOptimizationImageBlockWidgetSchema: z.ZodType<CheckoutOptimizationImageBlockWidget> =
  CheckoutOptimizationImageBlockWidgetSchema_;
export interface CheckoutOptimizationImageBlockWidget
  extends z.infer<typeof CheckoutOptimizationImageBlockWidgetSchema_> {}

const CheckoutOptimizationShippingOptionContentWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT),
  _id: zExt.objectId(),
  target: createEnum(ShippingOptionRenderTarget),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string().nullish(),
  backgroundColor: z.string(),
});
export const CheckoutOptimizationShippingOptionContentWidgetSchema: z.ZodType<CheckoutOptimizationShippingOptionContentWidget> =
  CheckoutOptimizationShippingOptionContentWidgetSchema_;
export interface CheckoutOptimizationShippingOptionContentWidget
  extends z.infer<
    typeof CheckoutOptimizationShippingOptionContentWidgetSchema_
  > {}

export const SHIPPING_OPTION_IMAGE_WIDTH_FULL = "FULL";

export const shippingOptionImageWidthOptions = [
  "30",
  "60",
  "90",
  "120",
  "180",
  "240",
  "300",
  "360",
  SHIPPING_OPTION_IMAGE_WIDTH_FULL,
] as const;
export type ShippingOptionImageWidth =
  (typeof shippingOptionImageWidthOptions)[number];

export const SHIPPING_OPTION_IMAGE_DEFAULT_WIDTH: ShippingOptionImageWidth =
  "180";

export const shippingOptionImageWidthToDisplayText: Record<
  ShippingOptionImageWidth,
  string
> = {
  "30": "30px",
  "60": "60px",
  "90": "90px",
  "120": "120px",
  "180": "180px",
  "240": "240px",
  "300": "300px",
  "360": "360px",
  [SHIPPING_OPTION_IMAGE_WIDTH_FULL]: "Full width",
};

const CheckoutOptimizationShippingOptionImageWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE),
  _id: zExt.objectId(),
  target: createEnum(ShippingOptionRenderTarget),
  imageUrl: z.string().nullish(),
  imageWidth: z.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});
export const CheckoutOptimizationShippingOptionImageWidgetSchema: z.ZodType<CheckoutOptimizationShippingOptionImageWidget> =
  CheckoutOptimizationShippingOptionImageWidgetSchema_;
export interface CheckoutOptimizationShippingOptionImageWidget
  extends z.infer<
    typeof CheckoutOptimizationShippingOptionImageWidgetSchema_
  > {}

export const FAILED_DISCOUNT_RESCUE_WIDGET_DEFAULT_TEXT =
  "Discount didn't work as expected? Try this one below.";
export const FAILED_DISCOUNT_RESCUE_WIDGET_DEFAULT_BUTTON_TEXT =
  "Claim discount";

const CheckoutOptimizationFailedDiscountRescueWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE),
  _id: zExt.objectId(),
  /**
   * Mongo ObjectId of the merchant-configured Discount this widget should
   * apply when the buyer claims a rescue. Optional because a newly-created
   * widget may not have a discount selected yet.
   */
  discountId: zExt.objectId().optional(),
  /**
   * Banner text shown to the buyer when a failed discount attempt is
   * detected. Default: "Discount didn't work as expected? Try this one
   * below."
   */
  text: z.string(),
  /** Label on the Claim button. Default: "Claim discount" */
  buttonText: z.string(),
});
export const CheckoutOptimizationFailedDiscountRescueWidgetSchema: z.ZodType<CheckoutOptimizationFailedDiscountRescueWidget> =
  CheckoutOptimizationFailedDiscountRescueWidgetSchema_;
export interface CheckoutOptimizationFailedDiscountRescueWidget
  extends z.infer<
    typeof CheckoutOptimizationFailedDiscountRescueWidgetSchema_
  > {}

const CheckoutOptimizationProductReviewsWidgetSchema_ = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.PRODUCT_REVIEWS),
  _id: zExt.objectId(),
  provider: createEnum(CheckoutReviewsProvider),
  minRating: z.number().int().min(1).max(5),
  display: z.object({
    showProductName: z.boolean(),
    showReviewTitle: z.boolean(),
    showReviewBody: z.boolean(),
    showReviewerName: z.boolean(),
    showVerifiedBadge: z.boolean(),
  }),
});
export const CheckoutOptimizationProductReviewsWidgetSchema: z.ZodType<CheckoutOptimizationProductReviewsWidget> =
  CheckoutOptimizationProductReviewsWidgetSchema_;
export interface CheckoutOptimizationProductReviewsWidget
  extends z.infer<typeof CheckoutOptimizationProductReviewsWidgetSchema_> {}

const WidgetTargetConfigSchema_ = z.object({
  widgets: z.array(
    z.discriminatedUnion("type", [
      CheckoutOptimizationFulfillmentCutoffWidgetSchema_,
      CheckoutOptimizationThresholdUpsellWidgetSchema_,
      CheckoutOptimizationLowProfileProductUpsellWidgetSchema_,
      CheckoutOptimizationContentBlockWidgetSchema_,
      CheckoutOptimizationImageBlockWidgetSchema_,
      CheckoutOptimizationFailedDiscountRescueWidgetSchema_,
      CheckoutOptimizationProductReviewsWidgetSchema_,
    ]),
  ),
});
interface WidgetTargetConfig
  extends z.infer<typeof WidgetTargetConfigSchema_> {}
const WidgetTargetConfigSchema: z.ZodType<WidgetTargetConfig> =
  WidgetTargetConfigSchema_;
export type WidgetTargetConfigWidgets = WidgetTargetConfig["widgets"];

/**
 * `targetToConfig` is persisted by Mongoose as an embedded document, so reads
 * include a Mongoose `_id` (and occasionally `__v`) key alongside the real
 * render-target keys. The previous fixed-key `z.object` schema silently
 * stripped those unknown keys; the free-string `z.record` below would instead
 * try to validate `_id`/`__v` as a widget config and fail, which silently drops
 * the entire checkout experience downstream. Strip the Mongoose meta keys
 * before parsing so existing persisted configs continue to read correctly.
 */
function omitMongooseMetaKeys(value: unknown): unknown {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return Object.fromEntries(
      Object.entries(value).filter(([key]) => key !== "_id" && key !== "__v"),
    );
  }
  return value;
}

const CheckoutWidgetsConfigSchema_ = z.object({
  // `targetToConfig` is keyed by render target. Historically these keys were
  // limited to `PurchaseCheckoutBlockRenderTarget` values, but merchants can
  // now place widgets at arbitrary string targets (e.g. "block1"), so the key
  // is a free string. The enum values remain the known/default targets.
  // A record tolerates absent keys, so legacy documents missing newer targets
  // (e.g. REDUCTIONS_BEFORE/AFTER) continue to validate; readers use optional
  // chaining.
  targetToConfig: z.preprocess(
    omitMongooseMetaKeys,
    z.record(z.string(), WidgetTargetConfigSchema),
  ),
});
export const CheckoutWidgetsConfigSchema: z.ZodType<CheckoutWidgetsConfig> =
  CheckoutWidgetsConfigSchema_;
export interface CheckoutWidgetsConfig
  extends z.infer<typeof CheckoutWidgetsConfigSchema_> {}

/**
 * Icons available for content block sections.
 * These can be used as imageUrl values in the database.
 */
export const contentBlockIcon = [
  "truck",
  "package",
  "checkCircle",
  "shieldTick",
  "lock",
  "clock",
  "gift",
  "star",
  "globe",
  "thumbsUp",
] as const;
export type ContentBlockIcon = (typeof contentBlockIcon)[number];

const DEFAULT_ICON_COLOR = "%23000000";

/**
 * SVG data URLs for content block icons.
 * Use these as imageUrl values in the database for Content Block sections.
 */
export const contentBlockIconSvgDataUrl: Record<ContentBlockIcon, string> = {
  truck: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 7H16.3373C16.5818 7 16.7041 7 16.8192 7.02763C16.9213 7.05213 17.0188 7.09253 17.1083 7.14736C17.2092 7.2092 17.2957 7.29568 17.4686 7.46863L21.5314 11.5314C21.7043 11.7043 21.7908 11.7908 21.8526 11.8917C21.9075 11.9812 21.9479 12.0787 21.9724 12.1808C22 12.2959 22 12.4182 22 12.6627V15.5C22 15.9659 22 16.1989 21.9239 16.3827C21.8224 16.6277 21.6277 16.8224 21.3827 16.9239C21.1989 17 20.9659 17 20.5 17M15.5 17H14M14 17V7.2C14 6.0799 14 5.51984 13.782 5.09202C13.5903 4.71569 13.2843 4.40973 12.908 4.21799C12.4802 4 11.9201 4 10.8 4H5.2C4.0799 4 3.51984 4 3.09202 4.21799C2.71569 4.40973 2.40973 4.71569 2.21799 5.09202C2 5.51984 2 6.0799 2 7.2V15C2 16.1046 2.89543 17 4 17M14 17H10M10 17C10 18.6569 8.65685 20 7 20C5.34315 20 4 18.6569 4 17M10 17C10 15.3431 8.65685 14 7 14C5.34315 14 4 15.3431 4 17M20.5 17.5C20.5 18.8807 19.3807 20 18 20C16.6193 20 15.5 18.8807 15.5 17.5C15.5 16.1193 16.6193 15 18 15C19.3807 15 20.5 16.1193 20.5 17.5Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  package: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.5 7.27783L16.25 9.63894M12 12.0001L3.49997 7.27783M12 12.0001L12 21.5001M12 12.0001L16.25 9.63894M7.52297 4.48732L3.82297 6.54288C3.52345 6.70928 3.37369 6.79248 3.26463 6.91082C3.16816 7.01551 3.09515 7.13959 3.05048 7.27477C3 7.42757 3 7.59889 3 7.94153V16.0586C3 16.4013 3 16.5726 3.05048 16.7254C3.09515 16.8606 3.16816 16.9847 3.26463 17.0893C3.37369 17.2077 3.52345 17.2909 3.82297 17.4573L11.223 21.5684C11.5066 21.726 11.6484 21.8047 11.7986 21.8356C11.9315 21.863 12.0685 21.863 12.2015 21.8356C12.3516 21.8047 12.4934 21.726 12.777 21.5684L20.177 17.4573C20.4766 17.2909 20.6263 17.2077 20.7354 17.0893C20.8318 16.9847 20.9049 16.8606 20.9495 16.7254C21 16.5726 21 16.4013 21 16.0586V7.94153C21 7.59889 21 7.42757 20.9495 7.27477C20.9049 7.13959 20.8318 7.01551 20.7354 6.91082C20.6263 6.79248 20.4766 6.70928 20.177 6.54288L12.777 2.43177C12.4934 2.27421 12.3516 2.19543 12.2015 2.16454C12.0685 2.13721 11.9315 2.13721 11.7986 2.16454C11.6484 2.19543 11.5066 2.27421 11.223 2.43177L7.52297 4.48732ZM7.52297 4.48732L16.25 9.63894" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  checkCircle: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  shieldTick: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 11.5L11 13.5L15.5 9M20 12C20 16.9084 14.646 20.4784 12.698 21.6147C12.4766 21.7439 12.3659 21.8085 12.2097 21.842C12.0884 21.868 11.9116 21.868 11.7903 21.842C11.6341 21.8085 11.5234 21.7439 11.302 21.6147C9.354 20.4784 4 16.9084 4 12V7.21747C4 6.41796 4 6.0182 4.13076 5.67457C4.24627 5.37101 4.43398 5.10015 4.67766 4.8854C4.9535 4.64231 5.32779 4.50195 6.07640 4.22122L11.4382 2.21054C11.6461 2.13258 11.75 2.0936 11.857 2.07815C11.9518 2.06444 12.0482 2.06444 12.143 2.07815C12.25 2.0936 12.3539 2.13258 12.5618 2.21054L17.9236 4.22122C18.6722 4.50195 19.0465 4.64231 19.3223 4.8854C19.566 5.10015 19.7537 5.37101 19.8692 5.67457C20 6.0182 20 6.41796 20 7.21747V12Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  lock: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 10V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V10M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  clock: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  gift: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 6V22M12 6H8.46429C7.94332 6 7.4437 5.78929 7.07533 5.41421C6.70695 5.03914 6.5 4.53043 6.5 4C6.5 3.46957 6.70695 2.96086 7.07533 2.58579C7.4437 2.21071 7.94332 2 8.46429 2C11.2143 2 12 6 12 6ZM12 6H15.5357C16.0567 6 16.5563 5.78929 16.9247 5.41421C17.293 5.03914 17.5 4.53043 17.5 4C17.5 3.46957 17.293 2.96086 16.9247 2.58579C16.5563 2.21071 16.0567 2 15.5357 2C12.7857 2 12 6 12 6ZM20 11V18.8C20 19.9201 20 20.4802 19.782 20.908C19.5903 21.2843 19.2843 21.5903 18.908 21.782C18.4802 22 17.9201 22 16.8 22L7.2 22C6.07989 22 5.51984 22 5.09202 21.782C4.71569 21.5903 4.40973 21.2843 4.21799 20.908C4 20.4802 4 19.9201 4 18.8V11M2 7.6L2 9.4C2 9.96005 2 10.2401 2.10899 10.454C2.20487 10.6422 2.35785 10.7951 2.54601 10.891C2.75992 11 3.03995 11 3.6 11L20.4 11C20.9601 11 21.2401 11 21.454 10.891C21.6422 10.7951 21.7951 10.6422 21.891 10.454C22 10.2401 22 9.96005 22 9.4V7.6C22 7.03995 22 6.75992 21.891 6.54601C21.7951 6.35785 21.6422 6.20487 21.454 6.10899C21.2401 6 20.9601 6 20.4 6L3.6 6C3.03995 6 2.75992 6 2.54601 6.10899C2.35785 6.20487 2.20487 6.35785 2.10899 6.54601C2 6.75992 2 7.03995 2 7.6Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  star: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.2827 3.45332C11.5131 2.98638 11.6284 2.75291 11.7848 2.67831C11.9209 2.61341 12.0791 2.61341 12.2152 2.67831C12.3717 2.75291 12.4869 2.98638 12.7174 3.45332L14.9041 7.88328C14.9721 8.02113 15.0061 8.09006 15.0558 8.14358C15.0999 8.19096 15.1527 8.22935 15.2113 8.25662C15.2776 8.28742 15.3536 8.29854 15.5057 8.32077L20.397 9.03571C20.9121 9.11099 21.1696 9.14863 21.2888 9.27444C21.3925 9.38389 21.4412 9.5343 21.4215 9.68377C21.3988 9.85558 21.2124 10.0372 20.8395 10.4004L17.3014 13.8464C17.1912 13.9538 17.136 14.0076 17.1004 14.0715C17.0689 14.128 17.0487 14.1902 17.0409 14.2545C17.0321 14.3271 17.0451 14.403 17.0711 14.5547L17.906 19.4221C17.994 19.9355 18.038 20.1922 17.9553 20.3445C17.8833 20.477 17.7554 20.57 17.6071 20.5975C17.4366 20.6291 17.2061 20.5078 16.7451 20.2654L12.3724 17.9658C12.2361 17.8942 12.168 17.8584 12.0962 17.8443C12.0327 17.8318 11.9673 17.8318 11.9038 17.8443C11.832 17.8584 11.7639 17.8942 11.6277 17.9658L7.25492 20.2654C6.79392 20.5078 6.56341 20.6291 6.39297 20.5975C6.24468 20.57 6.11672 20.477 6.04474 20.3445C5.962 20.1922 6.00603 19.9355 6.09407 19.4221L6.92889 14.5547C6.95491 14.403 6.96793 14.3271 6.95912 14.2545C6.95132 14.1902 6.93111 14.128 6.89961 14.0715C6.86402 14.0076 6.80888 13.9538 6.69859 13.8464L3.16056 10.4004C2.78766 10.0372 2.60121 9.85558 2.57853 9.68377C2.55879 9.5343 2.60755 9.38389 2.71125 9.27444C2.83044 9.14863 3.08797 9.11099 3.60304 9.03571L8.49431 8.32077C8.64642 8.29854 8.72248 8.28742 8.78872 8.25662C8.84736 8.22935 8.90016 8.19096 8.94419 8.14358C8.99391 8.09006 9.02793 8.02113 9.09597 7.88328L11.2827 3.45332Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  globe: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.2235 10H7.1629C7.0691 10.3472 7.0146 10.7214 7.0007 11.1209C6.9942 11.2527 6.9942 11.3865 7.0007 11.5184C7.0146 11.9179 7.0691 12.2921 7.1629 12.6393H3.2235C3.0776 12.1433 3 11.6126 3 11.0645C3 10.6874 3.0259 10.3558 3.2235 10ZM3.936 8H7.5797C8.0389 6.329 8.7627 4.738 9.7262 3.29C6.61 4.11 4.0813 6.73 3.936 8ZM12 3.551C10.9608 4.89 10.1725 6.398 9.6649 8H14.3362C13.8287 6.398 13.0404 4.89 12 3.551ZM14.8146 10H9.1863C9.078 10.6582 9.0157 11.3263 9.0007 12C9.0157 12.6737 9.078 13.3418 9.1863 14H14.8146C14.9228 13.3418 14.9852 12.6737 15.0002 12C14.9852 11.3263 14.9228 10.6582 14.8146 10ZM16.838 12.6393C16.9318 12.2921 16.9863 11.9179 17.0002 11.5184C17.0067 11.3865 17.0067 11.2527 17.0002 11.1209C16.9863 10.7214 16.9318 10.3472 16.838 10H20.7775C20.9751 10.3558 21.0009 10.6874 21.0009 11.0645C21.0009 11.6126 20.9233 12.1433 20.7775 12.6393H16.838ZM14.3362 16H9.6649C10.1725 17.602 10.9608 19.11 12 20.449C13.0404 19.11 13.8287 17.602 14.3362 16ZM9.7262 20.71C8.7627 19.262 8.0389 17.671 7.5797 16H3.936C4.0813 17.27 6.61 19.89 9.7262 20.71ZM14.2748 20.71C15.2383 19.262 15.9621 17.671 16.4213 16H20.065C19.9197 17.27 17.3909 19.89 14.2748 20.71ZM20.065 8H16.4213C15.9621 6.329 15.2383 4.738 14.2748 3.29C17.3909 4.11 19.9197 6.73 20.065 8ZM1 11.0645C1 5.92492 5.9253 1 12 1C18.0756 1 23.001 5.92492 23.001 11.0645C23.001 17.0751 18.0756 23 12 23C5.9253 23 1 17.0751 1 11.0645Z" fill="${DEFAULT_ICON_COLOR}"/></svg>`,
  thumbsUp: `data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 22V11M2 13V20C2 21.1046 2.89543 22 4 22H17.4262C18.907 22 20.1662 20.9197 20.3914 19.4562L21.4683 12.4562C21.7479 10.6389 20.3418 9 18.5032 9H15C14.4477 9 14 8.55228 14 8V4.46584C14 3.10399 12.896 2 11.5342 2C11.2093 2 10.915 2.1913 10.7831 2.48812L7.26394 10.4061C7.10344 10.7673 6.74532 11 6.35013 11H4C2.89543 11 2 11.8954 2 13Z" stroke="${DEFAULT_ICON_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
};
